import { Component, OnInit } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { CaseParticipantUriBuilderParams, ParticipantsService } from '@app/core/services/participants.service';
import { Participant } from '@app/shared/models/participant';
import { BaseWithIdList } from '@app/shared/models/list';
import * as _ from 'lodash-es';
import { ParticipantTypes } from '@app/shared/models/participant-type';
import { SelectComponentOptions } from '@app/shared/components/select/base-select-with-id.component';
import { CaseComponent } from '@app/modules/case/components/case-base/case.component';
import { CaseService } from '@app/core/services/case.service';
import { ActionListBtnConfig, ActionListOption } from '@app/shared/components/action-list/action-list.component';
import {NotificationService} from "@app/core/services/notification.service";

@Component({
  templateUrl: './case-detailed-participants.component.html',
  styleUrls: ['./case-detailed-participants.component.scss']
})
export class CaseDetailedParticipantsComponent extends CaseComponent implements OnInit {

  public participants: BaseWithIdList<Participant>;
  public newParticipantPanelOpen: Boolean = false;
  public participantTypeSelectOptions: SelectComponentOptions;
  public selectedParticipant: Participant;
  public participantTypeUriBuilderParams: CaseParticipantUriBuilderParams;

  public readonly actionListOptions: ActionListBtnConfig<Participant>[] = [{
    option: ActionListOption.DELETE,
    order: 1
  }, {
    option: 'AB_CREATE',
    order: 2,
    label: 'detailed_processing.participant.label.create_address_book',
    action: p => this.createAddressBookEntry(p),
    show: p => !this.isAddressBookEntryImported(p)
  }, {
    option: 'AB_UPDATE',
    order: 3,
    label: 'detailed_processing.participant.label.update_address_book',
    action: p => this.updateAddressBookEntry(p),
    show: p => this.isAddressBookEntryUpdatePossible(p)
  }];

  get caseId() {
    return this.id;
  }

  get ParticipantTypes() {
    return ParticipantTypes;
  }

  constructor(
      protected componentServiceBundle: ComponentServiceBundle,
      protected caseService: CaseService,
      protected participantsService: ParticipantsService,

  ) {
    super(componentServiceBundle, caseService);
    this._idPrefix = 'case_detailed_participants.';

    this.participantTypeSelectOptions = {
      optional: false
    };
  }

  public ngOnInit() {
    super.ngOnInit();
    this.participantTypeUriBuilderParams = new CaseParticipantUriBuilderParams(this.caseId);
  }

  protected whenOriginalFetched(): void {
    super.whenOriginalFetched();

    this.participantsService.query(new CaseParticipantUriBuilderParams(this.caseId))
        .subscribe( (participants: BaseWithIdList<Participant>) => {
          this.participants = participants;
          this.filterParticipants();
          this.sortParticipants();

          if (this.participants.hasItems) {
            this.openParticipant(this.participants.items[0]);
          }
        });
  }

  protected sortParticipants(): void {
    this.participants.items = _.sortBy(this.participants.items, (part) => {
      part.actionListDisplayName = this.participantsService.getTranslatedParticipantTypes(part);
      return part.actionListDisplayName;
    });
  }

  protected removeParticipantFromList(participant: Participant): void {
    this.participants.items = this.participants.items.filter(part => part.id !== participant.id);
  }

  public closeNewParticipantPanel(): void {
    this.newParticipantPanelOpen = false;
  }

  public openNewParticipantPanel(): void {
    this.newParticipantPanelOpen = true;
  }

  public openParticipant(participant: Participant) {
    this.selectedParticipant = null;

    setTimeout(() => {
      this.selectedParticipant = participant;
    }, 0);
  }

  public deleteParticipant(participant: Participant) {
    this.participantsService.delete(new CaseParticipantUriBuilderParams(this.caseId, participant))
        .subscribe((result) => {
          if (this.selectedParticipant && this.selectedParticipant.id === participant.id) {
            this.selectedParticipant = null;
          }
          this.removeParticipantFromList(participant);
        }, err => this.handleError(err));
  }

  public createParticipant(type: string) {
    if (type) {
      this.participantsService.createParticipant(this.caseId, {type: [type]})
          .subscribe(response => {
            const newParticipant = new Participant(response.data);
            this.participants.addItem( newParticipant );
            this.sortParticipants();
            this.closeNewParticipantPanel();
            this.openParticipant(newParticipant);
          });
    }
  }

  public allTypesCreated(allowedAmount: number): boolean {
    if (this.participants && this.participants.items) {
      let count = 0;

      for (let i = 0; i < this.participants.items.length; i++) {
        count += this.participants.items[i].type.length;
      }

      return count >= allowedAmount;
    } else {
      return false;
    }
  }

  protected filterParticipants(): void {
    // No filtering needed, all participants are shown on this page
    // Override in subclass if needed
  }

  public createAddressBookEntry(participant: Participant): void {
    this.participantsService.postContacts(participant.case_id, participant.id)
        .subscribe(participant => {
          this.componentServiceBundle.notificationService.showSimpleInfoDialog(
              'detailed_processing.participant.title.success',
              'detailed_processing.participant.label.create_address_book_success',
              '30%'
          );
        })
    ;
  }

  public updateAddressBookEntry(participant: Participant): void {
    this.participantsService.putContacts(participant.case_id, participant.id)
        .subscribe(participantResponse => {
          this.componentServiceBundle.notificationService.showSimpleInfoDialog(
              'detailed_processing.participant.title.success',
              'detailed_processing.participant.label.update_address_book_success',
              '30%'
          );
          participant.modified = !!participantResponse.data.modified;
        })
    ;
  }

  public isAddressBookEntryUpdatePossible(participant: Participant): boolean {
    return !!participant.modified;
  }

  public isAddressBookEntryImported(participant: Participant): boolean {
    return !!participant.source_id;
  }

  public isParticipantModified(participant: Participant): boolean {
    return participant.modified && participant.modified === true;
  }

}
